/**
 * The kit's document state: the cave on the bench, its undo history, live validation, whether it
 * has unsaved changes, the status-line message, and the working copy written behind the scenes.
 */

import { buildCave } from '../../caves/build';
import type { CaveDef } from '../../caves/types';
import type { CaveIssue } from '../../caves/validate';
import { diamondPotential, validateCave } from '../../caves/validate';
import type { KeyValueStore } from '../../platform/storage';
import type { KitDoc } from '../model/doc';
import { docSignature, docToDef } from '../model/doc';
import { History } from '../model/history';
import { INK } from './layout';
import { saveWorkingCopy } from './workingCopy';

export interface Analysis {
  readonly doc: KitDoc;
  readonly def: CaveDef;
  readonly issues: readonly CaveIssue[];
  readonly errors: number;
  /** Diamonds on the map at level 1 (after the random fill), and all that could be had. */
  readonly diamonds: number;
  readonly potential: number;
  /** Changes whenever the saved cave would. */
  readonly sig: string;
}

const SAVE_DELAY_MS = 700;

export class KitState {
  doc: KitDoc;
  readonly history = new History<KitDoc>();
  /** The library entry this cave was loaded from or last saved to. */
  libraryKey: string | undefined;
  message: { text: string; ink: number; ms: number } | null = null;
  private cleanSig: string;
  private analysis: Analysis | null = null;
  private saveIn = -1;

  constructor(
    private readonly storage: KeyValueStore,
    private readonly announce: (text: string) => void,
    doc: KitDoc,
    key: string | undefined,
    dirty: boolean,
  ) {
    this.doc = doc;
    this.libraryKey = key;
    this.cleanSig = dirty ? '' : docSignature(doc);
  }

  /** An edit: `next` replaces the doc as one undo step (same `tag` in a row merges). */
  edit(next: KitDoc, tag?: string): boolean {
    if (next === this.doc) return false;
    this.history.record(this.doc, tag);
    this.set(next);
    return true;
  }

  /** Change the doc without an undo step (the middle of a brush stroke); `commit` ends it. */
  set(next: KitDoc): void {
    if (next === this.doc) return;
    this.doc = next;
    this.saveIn = SAVE_DELAY_MS;
  }

  /** A stroke that began at `before` is over: one undo step, if it changed anything. */
  commit(before: KitDoc): void {
    if (before !== this.doc) this.history.record(before);
  }

  undo(): boolean {
    const prev = this.history.undo(this.doc);
    if (prev) this.set(prev);
    return prev !== null;
  }

  redo(): boolean {
    const next = this.history.redo(this.doc);
    if (next) this.set(next);
    return next !== null;
  }

  /** Put a different cave on the bench: a fresh history. */
  load(doc: KitDoc, key: string | undefined, clean: boolean): void {
    this.doc = doc;
    this.libraryKey = key;
    this.history.clear();
    this.cleanSig = clean ? docSignature(doc) : '';
    this.saveIn = 0;
  }

  /** Its library entry is gone: the cave on the bench counts as unsaved now. */
  forget(): void {
    this.libraryKey = undefined;
    this.cleanSig = '';
    this.saveIn = 0;
  }

  markSaved(key: string): void {
    this.libraryKey = key;
    this.cleanSig = docSignature(this.doc);
    this.saveIn = 0;
  }

  get dirty(): boolean {
    return this.info.sig !== this.cleanSig;
  }

  /** The def, its issues and diamond counts; worked out once per doc. */
  get info(): Analysis {
    if (this.analysis?.doc === this.doc) return this.analysis;
    const def = docToDef(this.doc);
    const issues = validateCave(def, 'custom');
    const errors = issues.filter((i) => i.severity === 'error').length;
    let diamonds = 0;
    let potential = 0;
    if (this.doc.grid.w > 0 && this.doc.grid.h > 0) {
      const p = diamondPotential(buildCave(def, 1));
      diamonds = p.onMap;
      potential = p.potential;
    }
    this.analysis = { doc: this.doc, def, issues, errors, diamonds, potential, sig: JSON.stringify(def) };
    return this.analysis;
  }

  /** A message in the status line (and to screen readers). */
  say(text: string, ink: number = INK.bright, ms = 2800): void {
    this.message = { text, ink, ms };
    this.announce(text);
  }

  update(dtMs: number): void {
    if (this.message) {
      this.message.ms -= dtMs;
      if (this.message.ms <= 0) this.message = null;
    }
    if (this.saveIn >= 0) {
      this.saveIn -= dtMs;
      if (this.saveIn < 0) this.persist();
    }
  }

  /** Write the working copy now. */
  persist(): void {
    this.saveIn = -1;
    const key = this.libraryKey;
    const copy = { def: docToDef(this.doc), dirty: this.dirty };
    saveWorkingCopy(this.storage, key === undefined ? copy : { ...copy, key });
  }
}
